import { useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import {
  Clock,
  CheckCircle,
  PlayCircle,
  AlertTriangle,
  Calendar,
  User,
  Package,
  CloudOff,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { es } from "date-fns/locale";

interface WorkOrder {
  id: string;
  order_number: string;
  product_name?: string;
  customer?: string;
  status: string;
  priority?: number;
  quantity?: number;
  due_date?: string;
  created_at?: string;
  assigned_operator?: string;
  process_records?: any[];
  _pendingSync?: boolean;
}

interface WorkOrderKanbanViewProps {
  workOrders: WorkOrder[];
  onSelectOrder: (id: string) => void;
}

interface KanbanColumn {
  id: string;
  title: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  items: WorkOrder[];
}

const STATUS_CONFIG: Record<string, { title: string; icon: React.ReactNode; color: string; bgColor: string }> = {
  pending: {
    title: "Pendiente",
    icon: <Clock className="w-4 h-4" />,
    color: "text-yellow-600",
    bgColor: "bg-yellow-500/10 border-yellow-500/30",
  },
  in_progress: {
    title: "En Progreso",
    icon: <PlayCircle className="w-4 h-4" />,
    color: "text-blue-600",
    bgColor: "bg-blue-500/10 border-blue-500/30",
  },
  completed: {
    title: "Completado",
    icon: <CheckCircle className="w-4 h-4" />,
    color: "text-green-600",
    bgColor: "bg-green-500/10 border-green-500/30",
  },
  cancelled: {
    title: "Cancelado",
    icon: <AlertTriangle className="w-4 h-4" />,
    color: "text-red-600",
    bgColor: "bg-red-500/10 border-red-500/30",
  },
};

const PRIORITY_CONFIG: Record<number, { label: string; color: string }> = {
  1: { label: "Baja", color: "text-gray-500 bg-gray-500/10 border-gray-500/20" },
  2: { label: "Normal", color: "text-blue-500 bg-blue-500/10 border-blue-500/20" },
  3: { label: "Alta", color: "text-orange-500 bg-orange-500/10 border-orange-500/20" },
  4: { label: "Urgente", color: "text-red-500 bg-red-500/10 border-red-500/20" },
};

export function WorkOrderKanbanView({ workOrders, onSelectOrder }: WorkOrderKanbanViewProps) {
  const columns = useMemo<KanbanColumn[]>(() => {
    const grouped: Record<string, WorkOrder[]> = {};

    workOrders.forEach((order) => {
      const status = order.status || "pending";
      if (!grouped[status]) {
        grouped[status] = [];
      }
      grouped[status].push(order);
    });

    // Orden de columnas
    const statusOrder = ["pending", "in_progress", "completed", "cancelled"];

    return statusOrder
      .filter((status) => grouped[status]?.length > 0 || ["pending", "in_progress", "completed"].includes(status))
      .map((status) => {
        const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
        return {
          id: status,
          title: config.title,
          icon: config.icon,
          color: config.color,
          bgColor: config.bgColor,
          items: grouped[status] || [],
        };
      });
  }, [workOrders]);

  const getOrderProgress = (order: WorkOrder) => {
    const records = order.process_records || [];
    if (records.length === 0) return 0;
    const completed = records.filter((r: any) => r.status === "completed").length;
    return Math.round((completed / records.length) * 100);
  };

  const getDaysUntilDue = (dueDate: string) => {
    const due = new Date(dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffTime = due.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  return (
    <ScrollArea className="w-full">
      <div className="flex gap-4 pb-4 min-w-max">
        {columns.map((column) => (
          <div
            key={column.id}
            className={cn("flex-shrink-0 w-80 rounded-lg border-2", column.bgColor)}
          >
            <div className={cn("flex items-center gap-2 p-3 border-b", column.color)}>
              {column.icon}
              <h3 className="font-semibold text-sm">{column.title}</h3>
              <Badge variant="secondary" className="ml-auto">
                {column.items.length}
              </Badge>
            </div>

            <ScrollArea className="h-[500px]">
              <div className="p-2 space-y-2">
                {column.items.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    Sin órdenes
                  </div>
                ) : (
                  column.items.map((order) => {
                    const progress = getOrderProgress(order);
                    const daysUntilDue = order.due_date
                      ? getDaysUntilDue(order.due_date)
                      : null;
                    const isOverdue = daysUntilDue !== null && daysUntilDue < 0;
                    const isUrgent = daysUntilDue !== null && daysUntilDue >= 0 && daysUntilDue <= 3;
                    const priorityConfig = PRIORITY_CONFIG[order.priority || 2];

                    return (
                      <Card
                        key={order.id}
                        className="cursor-pointer hover:shadow-md transition-shadow bg-card"
                        onClick={() => onSelectOrder(order.id)}
                      >
                        <CardContent className="p-3">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-sm">
                                {order.order_number}
                              </h4>
                              {order._pendingSync && (
                                <CloudOff className="w-3 h-3 text-amber-500" />
                              )}
                            </div>
                            {order.priority && order.priority !== 2 && (
                              <Badge variant="outline" className={cn("text-xs shrink-0", priorityConfig?.color)}>
                                {priorityConfig?.label}
                              </Badge>
                            )}
                          </div>

                          {order.product_name && (
                            <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                              {order.product_name}
                            </p>
                          )}

                          {order.customer && (
                            <div className="flex items-center gap-2 mb-2">
                              <User className="w-3 h-3 text-muted-foreground" />
                              <span className="text-xs text-muted-foreground truncate">
                                {order.customer}
                              </span>
                            </div>
                          )}

                          {order.quantity && (
                            <div className="flex items-center gap-2 mb-2">
                              <Package className="w-3 h-3 text-muted-foreground" />
                              <span className="text-xs text-muted-foreground">
                                {order.quantity} unidades
                              </span>
                            </div>
                          )}

                          {order.due_date && (
                            <div
                              className={cn(
                                "flex items-center gap-1 text-xs mb-2",
                                isOverdue
                                  ? "text-red-600"
                                  : isUrgent
                                  ? "text-yellow-600"
                                  : "text-muted-foreground"
                              )}
                            >
                              <Calendar className="w-3 h-3" />
                              {isOverdue
                                ? `Vencido hace ${Math.abs(daysUntilDue!)} días`
                                : daysUntilDue === 0
                                ? "Vence hoy"
                                : `Vence en ${daysUntilDue} días`}
                            </div>
                          )}

                          {order.process_records && order.process_records.length > 0 && (
                            <div className="space-y-1 mt-2 pt-2 border-t">
                              <div className="flex justify-between text-xs">
                                <span className="text-muted-foreground">Progreso</span>
                                <span className="font-medium">{progress}%</span>
                              </div>
                              <Progress value={progress} className="h-1.5" />
                            </div>
                          )}

                          {order.created_at && (
                            <div className="text-xs text-muted-foreground mt-2 pt-2 border-t">
                              Creada: {format(new Date(order.created_at), "dd MMM", { locale: es })}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </div>
            </ScrollArea>
          </div>
        ))}
      </div>
      <ScrollBar orientation="horizontal" />
    </ScrollArea>
  );
}
